/**
 * Purely visual/auditory edge FX for the Challenges lobby square:
 * nearness highlight, local push deformation with elastic rebound, and (blue-only) shatter.
 * Does not own blue/red lock state or real collision blocking.
 */

export type SqRect = { x: number; y: number; size: number };

type EdgeId = 0 | 1 | 2 | 3; // top, right, bottom, left

type ShatterBit = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  vr: number;
  life: number;
  maxLife: number;
  len: number;
  thick: number;
};

export type SquareFxState = {
  depth: number;
  depthVel: number;
  edge: EdgeId | null;
  along: number;
  nearAmt: number;
  contactAmt: number;
  shattered: boolean;
  shatterGap: number;
  bits: ShatterBit[];
  prevMx: number;
  prevMy: number;
  hasPrev: boolean;
  pressing: boolean;
};

const NEAR_PX = 42;
const CONTACT_PX = 14;
const MAX_DEPTH_BLUE = 58;
const MAX_DEPTH_RED = 64;
/** Any inward speed counts (px/s). Tiny so slow pushes still register. */
const INWARD_MOTION_MIN = 0.01;
/** Dent compression left before the blue outline counts as bottomed out. */
const BREAK_DEPTH_EPS = 1.5;
const INFLUENCE_FRAC = 0.22;
const SAMPLES_PER_SIDE = 28;
const STIFFNESS = 96;
const DAMPING_PRESS = 10;
const DAMPING_RELEASE = 5.5;
const OVERSHOOT_LIMIT = -16;
/** Keep cursor on same edge unless another is clearly closer (avoids corner flip). */
const EDGE_STICKY_PX = 10;
/** Min along inset from corners (fraction of side) — prevents corner pin. */
const EDGE_ALONG_INSET_MIN = 0.03;

export const SQUARE_FX_MAX_DEPTH_BLUE = MAX_DEPTH_BLUE;
export const SQUARE_FX_MAX_DEPTH_RED = MAX_DEPTH_RED;

const PERIM_SIDES: { edge: EdgeId; reverse: boolean }[] = [
  { edge: 0, reverse: false },
  { edge: 1, reverse: false },
  { edge: 2, reverse: true },
  { edge: 3, reverse: true },
];

export function createSquareFxState(): SquareFxState {
  return {
    depth: 0,
    depthVel: 0,
    edge: null,
    along: 0.5,
    nearAmt: 0,
    contactAmt: 0,
    shattered: false,
    shatterGap: 0,
    bits: [],
    prevMx: 0,
    prevMy: 0,
    hasPrev: false,
    pressing: false,
  };
}

export function resetSquareFx(fx: SquareFxState) {
  fx.depth = 0;
  fx.depthVel = 0;
  fx.edge = null;
  fx.along = 0.5;
  fx.nearAmt = 0;
  fx.contactAmt = 0;
  fx.shattered = false;
  fx.shatterGap = 0;
  fx.bits.length = 0;
  fx.hasPrev = false;
  fx.pressing = false;
}

function clamp(v: number, a: number, b: number) {
  return Math.max(a, Math.min(b, v));
}

function smoothstep(t: number) {
  const x = clamp(t, 0, 1);
  return x * x * (3 - 2 * x);
}

function edgePoint(sq: SqRect, edge: EdgeId, along: number): { x: number; y: number; nx: number; ny: number } {
  const t = clamp(along, 0, 1);
  const { x, y, size: s } = sq;
  if (edge === 0) return { x: x + t * s, y, nx: 0, ny: -1 };
  if (edge === 1) return { x: x + s, y: y + t * s, nx: 1, ny: 0 };
  if (edge === 2) return { x: x + t * s, y: y + s, nx: 0, ny: 1 };
  return { x, y: y + t * s, nx: -1, ny: 0 };
}

function influence(along: number, center: number, width: number) {
  const d = Math.abs(along - center);
  if (d >= width) return 0;
  return smoothstep(1 - d / width);
}

type FlatContact = {
  edge: EdgeId;
  along: number;
  cx: number;
  cy: number;
  nx: number;
  ny: number;
  dist: number;
};

function alongInset(sq: SqRect, cursorR: number) {
  return Math.max(EDGE_ALONG_INSET_MIN, (cursorR * 1.15) / Math.max(1, sq.size));
}

/** Project cursor onto one edge; clamp along away from corner singularities. */
function projectOntoEdge(px: number, py: number, sq: SqRect, edge: EdgeId, cursorR: number): number {
  const { x, y, size: s } = sq;
  const inset = alongInset(sq, cursorR);
  if (edge === 0) return clamp((px - x) / s, inset, 1 - inset);
  if (edge === 2) return clamp((px - x) / s, inset, 1 - inset);
  if (edge === 1) return clamp((py - y) / s, inset, 1 - inset);
  return clamp((py - y) / s, inset, 1 - inset);
}

/** Distance from point to one flat edge segment. */
function flatEdgeContact(px: number, py: number, sq: SqRect, edge: EdgeId, cursorR: number): FlatContact {
  const along = projectOntoEdge(px, py, sq, edge, cursorR);
  const p = edgePoint(sq, edge, along);
  const dist = Math.hypot(px - p.x, py - p.y);
  return { edge, along, cx: p.x, cy: p.y, nx: p.nx, ny: p.ny, dist };
}

/**
 * Stable flat-edge contact: sticky edge while pressing + corner-safe along clamp.
 */
function pickStableFlatContact(
  px: number,
  py: number,
  sq: SqRect,
  fx: SquareFxState | null,
  cursorR: number,
): FlatContact {
  const edges: EdgeId[] = [0, 1, 2, 3];
  let best = flatEdgeContact(px, py, sq, 0, cursorR);
  for (let i = 1; i < edges.length; i++) {
    const c = flatEdgeContact(px, py, sq, edges[i], cursorR);
    if (c.dist < best.dist) best = c;
  }

  if (fx?.edge != null && (fx.pressing || fx.depth > 1.5 || fx.contactAmt > 0.12)) {
    const cur = flatEdgeContact(px, py, sq, fx.edge, cursorR);
    if (cur.dist < best.dist + EDGE_STICKY_PX) {
      best = cur;
    }
  }

  return best;
}

/** Along position on an edge without corner inset (continuous for sink lookups). */
function projectAlongRaw(px: number, py: number, sq: SqRect, edge: EdgeId): number {
  const { x, y, size: s } = sq;
  if (edge === 0 || edge === 2) return clamp((px - x) / s, 0, 1);
  return clamp((py - y) / s, 0, 1);
}

/**
 * Closest point on the flat square boundary + outward unit direction.
 * `outward` is signed: positive outside, negative inside. Continuous everywhere
 * (radial around corners), so push-out resolution never snaps the cursor.
 */
function rectClosest(
  px: number,
  py: number,
  sq: SqRect,
): { cx: number; cy: number; nx: number; ny: number; outward: number } {
  const { x, y, size: s } = sq;
  const right = x + s;
  const bottom = y + s;

  if (px > x && px < right && py > y && py < bottom) {
    const dL = px - x;
    const dR = right - px;
    const dT = py - y;
    const dB = bottom - py;
    const m = Math.min(dL, dR, dT, dB);
    if (m === dT) return { cx: px, cy: y, nx: 0, ny: -1, outward: -m };
    if (m === dB) return { cx: px, cy: bottom, nx: 0, ny: 1, outward: -m };
    if (m === dL) return { cx: x, cy: py, nx: -1, ny: 0, outward: -m };
    return { cx: right, cy: py, nx: 1, ny: 0, outward: -m };
  }

  const cx = clamp(px, x, right);
  const cy = clamp(py, y, bottom);
  const dx = px - cx;
  const dy = py - cy;
  const d = Math.hypot(dx, dy);
  if (d <= 1e-4) {
    // Exactly on the boundary: fall back to the nearest edge normal.
    const dL = px - x;
    const dR = right - px;
    const dT = py - y;
    const dB = bottom - py;
    const m = Math.min(dL, dR, dT, dB);
    if (m === dT) return { cx, cy, nx: 0, ny: -1, outward: 0 };
    if (m === dB) return { cx, cy, nx: 0, ny: 1, outward: 0 };
    if (m === dL) return { cx, cy, nx: -1, ny: 0, outward: 0 };
    return { cx, cy, nx: 1, ny: 0, outward: 0 };
  }
  return { cx, cy, nx: dx / d, ny: dy / d, outward: d };
}

function deformedContactAt(
  sq: SqRect,
  fx: SquareFxState,
  edge: EdgeId,
  along: number,
  maxDepth: number,
  px: number,
  py: number,
) {
  const p = deformedBoundaryPoint(sq, edge, along, fx, maxDepth);
  return {
    edge,
    along,
    cx: p.x,
    cy: p.y,
    nx: p.nx,
    ny: p.ny,
    sink: p.sink,
    flatX: p.flatX,
    flatY: p.flatY,
    dist: Math.hypot(px - p.x, py - p.y),
  };
}

function localSinkAt(fx: SquareFxState, edge: EdgeId, along: number, maxDepth: number): number {
  if (fx.edge == null || edge !== fx.edge) return 0;
  const w = INFLUENCE_FRAC * (fx.shattered ? 1.25 : 1);
  const inf = influence(along, fx.along, w);
  if (inf <= 0) return 0;
  let sink = fx.depth * inf;
  if (fx.shattered && fx.shatterGap > 0) {
    sink += MAX_DEPTH_BLUE * 1.4 * inf * fx.shatterGap;
  }
  return Math.min(maxDepth, sink);
}

function deformedBoundaryPoint(
  sq: SqRect,
  edge: EdgeId,
  along: number,
  fx: SquareFxState,
  maxDepth: number,
): { x: number; y: number; nx: number; ny: number; sink: number; flatX: number; flatY: number } {
  const flat = edgePoint(sq, edge, along);
  const sink = localSinkAt(fx, edge, along, maxDepth);
  return {
    x: flat.x - flat.nx * sink,
    y: flat.y - flat.ny * sink,
    nx: flat.nx,
    ny: flat.ny,
    sink,
    flatX: flat.x,
    flatY: flat.y,
  };
}

/** Closest point on the *deformed* outline (visual pocket boundary). */
export function closestOnDeformedPerimeter(
  px: number,
  py: number,
  sq: SqRect,
  fx: SquareFxState,
  maxDepth: number,
): {
  edge: EdgeId;
  along: number;
  cx: number;
  cy: number;
  nx: number;
  ny: number;
  dist: number;
  sink: number;
  flatX: number;
  flatY: number;
} {
  let bestEdge: EdgeId = 0;
  let bestAlong = 0.5;
  let bestX = 0;
  let bestY = 0;
  let bestNx = 0;
  let bestNy = 0;
  let bestSink = 0;
  let bestFlatX = 0;
  let bestFlatY = 0;
  let bestDist = Infinity;

  for (const side of PERIM_SIDES) {
    for (let i = 0; i <= SAMPLES_PER_SIDE; i++) {
      const t = i / SAMPLES_PER_SIDE;
      const along = side.reverse ? 1 - t : t;
      const p = deformedBoundaryPoint(sq, side.edge, along, fx, maxDepth);
      const d = Math.hypot(px - p.x, py - p.y);
      if (d < bestDist) {
        bestDist = d;
        bestEdge = side.edge;
        bestAlong = along;
        bestX = p.x;
        bestY = p.y;
        bestNx = p.nx;
        bestNy = p.ny;
        bestSink = p.sink;
        bestFlatX = p.flatX;
        bestFlatY = p.flatY;
      }
    }
  }

  return {
    edge: bestEdge,
    along: bestAlong,
    cx: bestX,
    cy: bestY,
    nx: bestNx,
    ny: bestNy,
    dist: bestDist,
    sink: bestSink,
    flatX: bestFlatX,
    flatY: bestFlatY,
  };
}

/**
 * Block the cursor at the square barrier, letting it ride down the local dent.
 *
 * Resolution is a continuous closest-point push-out on the flat rect (radial around
 * corners), so sliding past a vertex never snaps the cursor to a new position.
 */
export function constrainCursorOnDeformedEdge(
  mx: number,
  my: number,
  sq: SqRect,
  fx: SquareFxState,
  cursorR: number,
  opts: { blocked: boolean; shattered?: boolean },
): { x: number; y: number } | null {
  if (opts.shattered) return null;

  const maxDepth = opts.blocked ? MAX_DEPTH_RED : MAX_DEPTH_BLUE;
  const rc = rectClosest(mx, my, sq);

  // Local dent depth lets the disc descend below the flat surface at that spot only.
  const sink =
    fx.edge != null
      ? localSinkAt(fx, fx.edge, projectAlongRaw(mx, my, sq, fx.edge), maxDepth)
      : 0;

  const required = cursorR - sink;
  if (rc.outward >= required) return null;

  return { x: rc.cx + rc.nx * required, y: rc.cy + rc.ny * required };
}

/**
 * Advance deformation / highlight. Returns true when a blue break should start the fade flow.
 */
export function tickSquareFx(
  fx: SquareFxState,
  opts: {
    sq: SqRect;
    mx: number;
    my: number;
    cursorR: number;
    dt: number;
    blocked: boolean;
    allowBreak: boolean;
  },
): boolean {
  const { sq, mx, my, cursorR, blocked, allowBreak } = opts;
  const dt = Math.min(0.05, Math.max(0, opts.dt));
  const maxDepth = blocked ? MAX_DEPTH_RED : MAX_DEPTH_BLUE;
  const wasPressing = fx.pressing;

  for (let i = fx.bits.length - 1; i >= 0; i--) {
    const b = fx.bits[i];
    b.life -= dt;
    if (b.life <= 0) {
      fx.bits.splice(i, 1);
      continue;
    }
    b.x += b.vx * dt;
    b.y += b.vy * dt;
    b.vx *= 0.98;
    b.vy *= 0.98;
    b.vy += 40 * dt;
    b.rot += b.vr * dt;
  }

  if (fx.shattered) {
    fx.nearAmt *= Math.exp(-10 * dt);
    fx.contactAmt *= Math.exp(-10 * dt);
    fx.shatterGap = Math.max(0, fx.shatterGap - dt * 0.35);
    const force = (0 - fx.depth) * STIFFNESS - fx.depthVel * DAMPING_RELEASE;
    fx.depthVel += force * dt;
    fx.depth += fx.depthVel * dt;
    fx.pressing = false;
    fx.hasPrev = true;
    fx.prevMx = mx;
    fx.prevMy = my;
    return false;
  }

  const flatHit = pickStableFlatContact(mx, my, sq, fx, cursorR);
  const deformHit =
    fx.edge != null && (fx.pressing || fx.depth > 1)
      ? deformedContactAt(
          sq,
          fx,
          fx.edge,
          projectOntoEdge(mx, my, sq, fx.edge, cursorR),
          maxDepth,
          mx,
          my,
        )
      : closestOnDeformedPerimeter(mx, my, sq, fx, maxDepth);
  const nearBand = NEAR_PX + cursorR * 0.35;
  const contactBand = CONTACT_PX + cursorR * 0.25;
  // Penetration measured on the flat rect with the same geometry as the constraint.
  const rc = rectClosest(mx, my, sq);
  const dist = Math.abs(rc.outward);
  const inward = -rc.outward;

  // Deeper than any dent can reach (cursor spawned inside): no press, just push out.
  const deepInside = inward > maxDepth + cursorR * 0.5;

  let near = 0;
  let contact = 0;
  if (!deepInside) {
    if (dist < nearBand) near = smoothstep(1 - dist / nearBand);
    // Once the disc reaches the wall it stays engaged, however deep the dent goes.
    near = Math.max(near, clamp((inward + nearBand) / nearBand, 0, 1));

    if (dist < contactBand) contact = smoothstep(1 - dist / contactBand);
    if (inward > -cursorR * 0.15) {
      contact = Math.max(contact, smoothstep(1 - Math.max(0, -inward) / contactBand));
    }
    if (deformHit.dist < contactBand + cursorR * 0.5) {
      contact = Math.max(contact, smoothstep(1 - deformHit.dist / (contactBand + cursorR * 0.5)));
    }
  }

  fx.nearAmt += (near - fx.nearAmt) * Math.min(1, dt * 14);
  fx.contactAmt += (contact - fx.contactAmt) * Math.min(1, dt * 18);

  let intoSpeed = 0;
  if (fx.hasPrev) {
    const vx = (mx - fx.prevMx) / Math.max(dt, 1 / 120);
    const vy = (my - fx.prevMy) / Math.max(dt, 1 / 120);
    // Prefer the active contact wall; fall back to closest flat edge.
    const nx = fx.edge != null ? deformHit.nx : flatHit.nx;
    const ny = fx.edge != null ? deformHit.ny : flatHit.ny;
    intoSpeed = vx * -nx + vy * -ny;
  }
  const movingIn = intoSpeed > INWARD_MOTION_MIN;

  const pressing =
    !deepInside && near > 0.12 && (movingIn || contact > 0.3 || inward > -cursorR * 0.45);
  fx.pressing = pressing;

  let targetDepth = 0;
  if (pressing) {
    fx.edge = flatHit.edge;
    // Dent center tracks cursor along the edge — stays glued while pushing.
    if (contact > 0.22 || inward > -cursorR * 0.2) {
      fx.along = flatHit.along;
    } else {
      const alongFollow = 1 - Math.exp(-dt * 32);
      fx.along += (flatHit.along - fx.along) * alongFollow;
    }

    // Wall stays glued to the disc: sink equals how far the cursor pushed in.
    // Slow, sustained pushes accumulate the same way as fast ones, up to maxDepth.
    targetDepth = clamp(inward + cursorR, 0, maxDepth);
  } else if (fx.edge != null && (Math.abs(fx.depth) > 0.4 || Math.abs(fx.depthVel) > 2)) {
    targetDepth = 0;
  } else {
    if (near > 0.08) {
      fx.edge = flatHit.edge;
      fx.along = flatHit.along;
    }
    targetDepth = 0;
  }

  // Extra outward kick when releasing a deep press (bouncier rebound).
  if (wasPressing && !pressing && fx.depth > maxDepth * 0.2) {
    fx.depthVel -= fx.depth * 1.15 + 6;
  }

  const damping = pressing ? DAMPING_PRESS : DAMPING_RELEASE;
  const force = (targetDepth - fx.depth) * STIFFNESS - fx.depthVel * damping;
  if (pressing && contact > 0.25) {
    const snap = 1 - Math.exp(-dt * 42);
    fx.depth += (targetDepth - fx.depth) * snap;
    fx.depthVel = (targetDepth - fx.depth) * 18;
  } else {
    fx.depthVel += force * dt;
    fx.depth += fx.depthVel * dt;
  }
  if (fx.depth > maxDepth) {
    fx.depth = maxDepth;
    if (fx.depthVel > 0) fx.depthVel = 0;
  }
  if (fx.depth < OVERSHOOT_LIMIT) {
    fx.depth = OVERSHOOT_LIMIT;
    if (fx.depthVel < 0) fx.depthVel = 0;
  }

  // Blue breaks the instant the dent bottoms out under the cursor.
  const bottomedOut = pressing && contact > 0.12 && fx.depth >= maxDepth - BREAK_DEPTH_EPS;

  let didBreak = false;
  if (allowBreak && !blocked && bottomedOut) {
    didBreak = true;
    beginShatter(fx, sq);
  }

  fx.hasPrev = true;
  fx.prevMx = mx;
  fx.prevMy = my;
  return didBreak;
}

function beginShatter(fx: SquareFxState, sq: SqRect) {
  fx.shattered = true;
  fx.shatterGap = 1;
  fx.depthVel = -52;
  const edge = fx.edge ?? 0;
  const along = fx.along;
  const width = INFLUENCE_FRAC * 1.15;
  const colorN = 14;
  for (let i = 0; i < colorN; i++) {
    const t = along + (Math.random() - 0.5) * width;
    const p = edgePoint(sq, edge, t);
    const spread = (Math.random() - 0.5) * 180;
    const speed = 90 + Math.random() * 160;
    const sign = Math.random() < 0.55 ? 1 : -1;
    const nx = p.nx * sign;
    const ny = p.ny * sign;
    const tx = -p.ny;
    const ty = p.nx;
    fx.bits.push({
      x: p.x + (Math.random() - 0.5) * 6,
      y: p.y + (Math.random() - 0.5) * 6,
      vx: nx * speed + tx * spread,
      vy: ny * speed + ty * spread,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 14,
      life: 0.45 + Math.random() * 0.55,
      maxLife: 0.7,
      len: 6 + Math.random() * 14,
      thick: 1.5 + Math.random() * 2.2,
    });
  }
}

function perimeterPoint(
  sq: SqRect,
  edge: EdgeId,
  along: number,
  fx: SquareFxState,
): { x: number; y: number } {
  // Depth is already capped per state; use the widest cap so red is not clipped.
  const p = deformedBoundaryPoint(sq, edge, along, fx, Math.max(MAX_DEPTH_BLUE, MAX_DEPTH_RED));
  return { x: p.x, y: p.y };
}

function strokeDeformedSquare(
  ctx: CanvasRenderingContext2D,
  sq: SqRect,
  fx: SquareFxState,
  style: { color: string; glow: string; lineWidth: number; blur: number; alpha?: number },
) {
  ctx.save();
  ctx.globalAlpha = style.alpha ?? 1;
  ctx.strokeStyle = style.color;
  ctx.lineWidth = style.lineWidth;
  ctx.shadowColor = style.glow;
  ctx.shadowBlur = style.blur;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.beginPath();
  let first = true;
  for (const side of PERIM_SIDES) {
    for (let i = 0; i < SAMPLES_PER_SIDE; i++) {
      const t = i / SAMPLES_PER_SIDE;
      const along = side.reverse ? 1 - t : t;
      const p = perimeterPoint(sq, side.edge, along, fx);
      if (first) {
        ctx.moveTo(p.x, p.y);
        first = false;
      } else {
        ctx.lineTo(p.x, p.y);
      }
    }
  }
  ctx.closePath();
  ctx.stroke();
  ctx.restore();
}

function strokeHighlightArc(
  ctx: CanvasRenderingContext2D,
  sq: SqRect,
  fx: SquareFxState,
  color: string,
  glow: string,
  intensity: number,
) {
  if (fx.edge == null || intensity < 0.04) return;
  const edge = fx.edge;
  const halfW = INFLUENCE_FRAC * (0.7 + 0.5 * intensity);
  const steps = Math.max(8, Math.floor(SAMPLES_PER_SIDE * halfW * 2.2));
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 2 + intensity * 3.5;
  ctx.shadowColor = glow;
  ctx.shadowBlur = 8 + intensity * 22;
  ctx.globalAlpha = 0.35 + intensity * 0.65;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  let started = false;
  for (let i = 0; i <= steps; i++) {
    const along = fx.along + (-halfW + (2 * halfW * i) / steps);
    if (along < 0 || along > 1) continue;
    const p = perimeterPoint(sq, edge, along, fx);
    if (!started) {
      ctx.moveTo(p.x, p.y);
      started = true;
    } else {
      ctx.lineTo(p.x, p.y);
    }
  }
  if (started) ctx.stroke();
  ctx.restore();
}

function drawShatterBits(
  ctx: CanvasRenderingContext2D,
  fx: SquareFxState,
  color: string,
  glow: string,
) {
  if (!fx.bits.length) return;
  ctx.save();
  for (const b of fx.bits) {
    const a = clamp(b.life / b.maxLife, 0, 1);
    ctx.save();
    ctx.translate(b.x, b.y);
    ctx.rotate(b.rot);
    ctx.globalAlpha = a;
    ctx.strokeStyle = color;
    ctx.shadowColor = glow;
    ctx.shadowBlur = 10;
    ctx.lineWidth = b.thick;
    ctx.beginPath();
    ctx.moveTo(-b.len * 0.5, 0);
    ctx.lineTo(b.len * 0.5, 0);
    ctx.stroke();
    ctx.restore();
  }
  ctx.restore();
}

/** Draw outline (+ optional inner glow) with deformation / highlight / shatter debris. */
export function drawSquareFxOutline(
  ctx: CanvasRenderingContext2D,
  sq: SqRect,
  fx: SquareFxState,
  opts: {
    blocked: boolean;
    color: string;
    glow: string;
    highlightColor: string;
    highlightGlow: string;
  },
) {
  const { blocked, color, glow, highlightColor, highlightGlow } = opts;
  const baseW = blocked ? 2.5 : 4;
  const baseBlur = blocked ? 10 : 28;

  strokeDeformedSquare(ctx, sq, fx, {
    color,
    glow,
    lineWidth: baseW,
    blur: baseBlur,
  });

  if (!blocked) {
    const savedDepth = fx.depth;
    fx.depth = savedDepth + 3;
    strokeDeformedSquare(ctx, sq, fx, {
      color: "rgba(220, 255, 255, 0.95)",
      glow,
      lineWidth: 1.5,
      blur: 14,
      alpha: 0.9,
    });
    fx.depth = savedDepth;
  }

  const hi = clamp(fx.nearAmt * 0.55 + fx.contactAmt * 0.7, 0, 1);
  if (hi > 0.05 && !fx.shattered) {
    const boost = fx.contactAmt > 0.45 ? 1 : 0.65;
    strokeHighlightArc(ctx, sq, fx, highlightColor, highlightGlow, hi * boost);
  }

  if (fx.depth > 1 && fx.edge != null && !fx.shattered) {
    const maxD = blocked ? MAX_DEPTH_RED : MAX_DEPTH_BLUE;
    const sinkHi = clamp(fx.depth / maxD, 0, 1) * (0.4 + fx.contactAmt * 0.6);
    strokeHighlightArc(ctx, sq, fx, highlightColor, highlightGlow, sinkHi);
  }

  drawShatterBits(ctx, fx, color, glow);
}
